import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from '../../utils/api';
import { PREVIEW_SANDBOX, openSandboxedPreviewWindow } from '../../utils/sandboxedPreview';
import { COMPONENT_PREVIEW_HEIGHT_MESSAGE, buildComponentPreviewDocument } from './componentTesterPreview';
import './ComponentTester.css';

interface Component {
  id: number;
  name: string;
  display_name: string;
  category: string;
  variant_code: string;
  component_name: string;
}

interface Theme {
  value: string;
  label: string;
}

interface RenderedComponent {
  success: boolean;
  component: {
    id: number;
    name: string;
    category: string;
    variant_code: string;
  };
  html: string;
  css: string;
  generatedContent: Record<string, any>;
  images: Record<string, any>;
  metadata: {
    generationTime: number;
    aiGenerationTime: number;
    imageSearchTime: number;
    imageProcessingTime: number;
    cached: boolean;
    timestamp: string;
  };
  pagination?: {
    currentPage: number;
    totalPages: number;
    total: number;
    hasMore: boolean;
    hasNext: boolean;
    hasPrev: boolean;
  };
  // 🎯 #F-084 MVP: Метрики производительности (опционально)
  metrics?: {
    totalDuration: number;
    startTime: string;
    endTime: string;
    stages: {
      images: {
        duration: number;
        percentage: number;
        attempts: number;
        totalRequested: number;
        totalReceived: number;
        totalDuplicates: number;
        attemptsDetails: Array<{
          attemptNumber: number;
          provider: string;
          requested: number;
          received: number;
          duplicatesRemoved: number;
          duration: number;
        }>;
      };
      texts: {
        duration: number;
        percentage: number;
      };
      rendering: {
        duration: number;
        percentage: number;
      };
    };
  };
}

// Состояние для batch режима
interface BatchResult {
  componentId: number;
  componentName: string;
  displayName: string;
  status: 'pending' | 'loading' | 'success' | 'error';
  error?: string;
  generationTime?: number;
}

const PREVIEW_FRAME_MIN_HEIGHT = 120;
const PREVIEW_FRAME_MAX_HEIGHT = 20000;
// Стартовая высота iframe ≈ экран: от неё считаются vh-размеры (hero с min-height: 100vh и т.п.).
const getInitialPreviewFrameHeight = () =>
  typeof window === 'undefined' ? 480 : Math.max(480, Math.round(window.innerHeight * 0.8));

export const ComponentTester: React.FC = () => {
  // Состояние
  const [components, setComponents] = useState<Component[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  
  const [selectedComponentId, setSelectedComponentId] = useState<number | null>(null);
  const [selectedTheme, setSelectedTheme] = useState<string>('corporate-blue');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('ru');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('facebook');
  const [selectedGeo, setSelectedGeo] = useState<string>('eu');
  
  // 🆕 #F-Background: Настройки глобального фона
  const [backgroundEnabled, setBackgroundEnabled] = useState<boolean>(false);
  const [backgroundMode, setBackgroundMode] = useState<'static' | 'dynamic'>('static');
  const [backgroundPerformance, setBackgroundPerformance] = useState<string>('balanced');
  const [backgroundPresetId, setBackgroundPresetId] = useState<number | null>(null); // dynamic preset
  const [backgroundCuratedId, setBackgroundCuratedId] = useState<number | null>(null); // static curated

  // Данные пресетов/изображений
  const [bgPresets, setBgPresets] = useState<any[]>([]);
  const [bgCurated, setBgCurated] = useState<any[]>([]);
  
  // Параметры фильтрации и пагинации (для gallery компонентов)
  const [galleryFilter, setGalleryFilter] = useState<string>('all');
  const [galleryPage, setGalleryPage] = useState<number>(1);
  const [galleryLimit, setGalleryLimit] = useState<number>(9);
  
  // 🎯 #F-084 MVP: Включение метрик производительности
  const [includeMetrics, setIncludeMetrics] = useState<boolean>(false);
  
  const [renderedData, setRenderedData] = useState<RenderedComponent | null>(null);
  const renderedHtml = renderedData?.html;
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingElapsed, setLoadingElapsed] = useState<number>(0);
  const [loadingMessage, setLoadingMessage] = useState<string>('Генерация компонента…');
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [showImageSources, setShowImageSources] = useState<boolean>(false);
  
  const loadingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeRequestController = useRef<AbortController | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const intervalRefs = useRef<ReturnType<typeof setInterval>[]>([]);

  const trackInterval = (callback: () => void, delay: number) => {
    if (!isMountedRef.current) {
      return null;
    }
    const id = setInterval(callback, delay);
    intervalRefs.current.push(id);
    return id;
  };

  const clearTrackedTimers = () => {
    intervalRefs.current.forEach((id) => clearInterval(id));
    intervalRefs.current = [];
  };

  const LONG_REQUEST_NOTICE_THRESHOLD = 30; // секунд
  const LONG_REQUEST_WARNING_THRESHOLD = 120; // секунд
  
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  
  // 🆕 Batch Mode - режим тестирования группы
  const [testMode, setTestMode] = useState<'single' | 'batch'>('single');
  const [batchResults, setBatchResults] = useState<BatchResult[]>([]);
  const [batchLoading, setBatchLoading] = useState<boolean>(false);
  const [batchProgress, setBatchProgress] = useState<{current: number, total: number}>({current: 0, total: 0});
  const batchAbortRef = useRef<boolean>(false);
  const [selectedBatchCategories, setSelectedBatchCategories] = useState<string[]>([]);

  // CSS тем: применяется только внутри sandboxed iframe превью, а не в документе приложения.
  const [themesCss, setThemesCss] = useState<string>('');
  const previewFrameRef = useRef<HTMLIFrameElement | null>(null);
  const previewSrcDocRef = useRef<string>('');
  // Высота, сообщённая iframe, привязана к своему документу: новый документ стартует с «экранной» высоты.
  const [previewFrameSize, setPreviewFrameSize] = useState<{ doc: string; height: number } | null>(null);

  useEffect(() => {
    const loadThemesCSS = async () => {
      try {
        const response = await axios.get('/api/admin/component-tester/themes-css');
        if (isMountedRef.current) {
          setThemesCss(typeof response.data === 'string' ? response.data : '');
        }
      } catch (error) {
        console.error('Ошибка загрузки CSS тем:', error);
      }
    };
    
    loadThemesCSS();
  }, []);

  // 📷 Источники изображений для плашек (сопоставление выполняется внутри iframe)
  const imageSourceEntries = useMemo<Array<[string, string]>>(() => {
    const imageSourceMap = new Map<string, string>();
    Object.values(renderedData?.images || {}).forEach((imageData: any) => {
      const images = Array.isArray(imageData) ? imageData : [imageData];
      images.forEach((img: any) => {
        if (typeof img?.url === 'string' && typeof img?.source === 'string') {
          // Нормализуем URL для сопоставления
          imageSourceMap.set(img.url.replace(/^https?:/, '').split('?')[0], img.source);
        }
      });
    });
    return Array.from(imageSourceMap.entries());
  }, [renderedData?.images]);

  // ИИ/пользовательский HTML исполняется только в iframe sandbox="allow-scripts" (без allow-same-origin):
  // тема, CSS компонента, карусель, карты и плашки источников живут внутри iframe.
  const previewSrcDoc = useMemo(() => {
    if (renderedHtml === undefined) return '';
    return buildComponentPreviewDocument({
      html: renderedHtml || '',
      css: renderedData?.css || '',
      themesCss,
      theme: selectedTheme,
      language: selectedLanguage,
      showImageSources,
      imageSources: imageSourceEntries
    });
  }, [renderedHtml, renderedData?.css, themesCss, selectedTheme, selectedLanguage, showImageSources, imageSourceEntries]);

  const previewFrameHeight =
    previewFrameSize && previewFrameSize.doc === previewSrcDoc ? previewFrameSize.height : getInitialPreviewFrameHeight();

  useEffect(() => {
    previewSrcDocRef.current = previewSrcDoc;
  }, [previewSrcDoc]);

  useEffect(() => {
    const handleFrameMessage = (event: MessageEvent) => {
      const frameWindow = previewFrameRef.current?.contentWindow;
      if (!frameWindow || event.source !== frameWindow) return;
      const data = event.data as { type?: unknown; height?: unknown } | null;
      if (!data || data.type !== COMPONENT_PREVIEW_HEIGHT_MESSAGE) return;
      if (typeof data.height !== 'number' || !Number.isFinite(data.height)) return;
      const height = Math.min(Math.max(Math.ceil(data.height), PREVIEW_FRAME_MIN_HEIGHT), PREVIEW_FRAME_MAX_HEIGHT);
      setPreviewFrameSize({ doc: previewSrcDocRef.current, height });
    };
    window.addEventListener('message', handleFrameMessage);
    return () => window.removeEventListener('message', handleFrameMessage);
  }, []);

  // Загрузка компонентов при монтировании
  useEffect(() => {
    fetchComponents();
  }, []);

  // Загружаем фоновые пресеты и curated изображения
  useEffect(() => {
    const loadBackgroundData = async () => {
      try {
        const [presetsRes, curatedRes] = await Promise.all([
          axios.get('/api/admin/component-tester/background-presets'),
          axios.get('/api/admin/component-tester/curated-backgrounds')
        ]);
        const presetsData = presetsRes.data;
        const curatedData = curatedRes.data;
        if (presetsData?.success) setBgPresets(presetsData.presets || []);
        if (curatedData?.success) setBgCurated(curatedData.backgrounds || []);
      } catch (err) {
        console.warn('⚠️ Не удалось загрузить данные фонов:', err);
      }
    };
    loadBackgroundData();
  }, []);
  
  // Сброс параметров фильтрации при смене компонента
  useEffect(() => {
    setGalleryFilter('all');
    setGalleryPage(1);
    setGalleryLimit(9);
  }, [selectedComponentId]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearTrackedTimers();
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      if (activeRequestController.current) {
        activeRequestController.current.abort();
      }
    };
  }, []);

  const fetchComponents = async () => {
    try {
      const response = await axios.get('/api/admin/component-tester/components');
      const data = response.data;
      
      if (data.success) {
        setComponents(data.components);
        setThemes(data.themes);
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Ошибка загрузки компонентов:', error);
    }
  };

  const stopLoadingState = () => {
    if (loadingTimerRef.current) {
      clearInterval(loadingTimerRef.current);
      loadingTimerRef.current = null;
    }
    setLoading(false);
    setLoadingElapsed(0);
    setLoadingMessage('Генерация компонента…');
    activeRequestController.current = null;
  };

  const startLoadingState = () => {
    setLoading(true);
    setLoadingElapsed(0);
    setLoadingMessage('Генерация компонента…');

    if (loadingTimerRef.current) {
      clearInterval(loadingTimerRef.current);
    }

    loadingTimerRef.current = trackInterval(() => {
      setLoadingElapsed(prev => {
        const next = prev + 1;

        if (next === LONG_REQUEST_NOTICE_THRESHOLD) {
          setLoadingMessage('⌛ Генерация занимает больше времени, продолжаем ожидание...');
        }

        if (next === LONG_REQUEST_WARNING_THRESHOLD) {
          setLoadingMessage('⚠️ Генерация длится более 2 минут. Можно дождаться окончания или отменить запрос.');
        }

        return next;
      });
    }, 1000);
  };

  const buildRequestPayload = (forceRegenerate: boolean) => {
    if (!selectedComponentId) {
      throw new Error('Компонент не выбран');
    }

    const selectedComponent = components.find(c => c.id === selectedComponentId);
    const isGallery = selectedComponent?.category === 'gallery' || selectedComponent?.category === 'portfolio';

    const requestBody: Record<string, any> = {
      componentId: selectedComponentId,
      theme: selectedTheme,
      contentLanguage: selectedLanguage,
      platform: selectedPlatform,
      geo: selectedGeo,
      forceRegenerate,
      includeMetrics, // 🎯 #F-084 MVP: Передаем параметр метрик
      // 🆕 Фон
      backgroundEnabled,
      backgroundMode,
      backgroundPerformance,
      ...(backgroundPresetId ? { backgroundPresetId } : {}),
      ...(backgroundCuratedId ? { backgroundCuratedId } : {})
    };

    if (isGallery) {
      requestBody.filter = galleryFilter;
      requestBody.page = galleryPage;
      requestBody.limit = galleryLimit;
    }

    return requestBody;
  };

  const clearComponentCache = async (componentId?: number | null) => {
    const query = componentId ? `?componentId=${componentId}` : '';
    await axios.delete(`/api/admin/component-tester/cache${query}`);
  };

  const clearImageCaches = async () => {
    try {
      await axios.post('/api/image-search/clear-cache');
    } catch (e) {
      console.warn('⚠️ Не удалось очистить кеш image-search', e);
    }
    try {
      await axios.delete('/api/image-processing/cache');
    } catch (e) {
      console.warn('⚠️ Не удалось очистить кеш image-processing', e);
    }
  };

  const performRenderRequest = async (forceRegenerate: boolean) => {
    try {
      const payload = buildRequestPayload(forceRegenerate);

      startLoadingState();
      if (!forceRegenerate) {
        setRenderedData(null);
      }

      const controller = new AbortController();
      activeRequestController.current = controller;

      const response = await axios.post('/api/admin/component-tester/render', payload, {
        signal: controller.signal
      });

      const data = response.data;

      if (data.success) {
        setRenderedData(data);
        setLoadingMessage('Компонент успешно сгенерирован');
      } else {
        throw new Error(data.error || 'Неизвестная ошибка при генерации');
      }
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.warn('Запрос генерации отменён пользователем');
        setLoadingMessage('⚠️ Запрос отменён');
      } else {
        console.error('Ошибка рендеринга:', error);
        const message = typeof error?.message === 'string' ? error.message : 'Неизвестная ошибка';
        alert(`Ошибка при рендеринге компонента: ${message}`);
        setLoadingMessage('⚠️ Возникла ошибка при генерации');
      }
    } finally {
      if (isMountedRef.current) {
        stopLoadingState();
      }
    }
  };

  const handleGenerate = async () => {
    if (!selectedComponentId) {
      alert('Пожалуйста, выберите компонент');
      return;
    }

    await performRenderRequest(false);
  };

  const handleRegenerate = async () => {
    if (!selectedComponentId) {
      alert('Пожалуйста, выберите компонент');
      return;
    }

    setLoadingMessage('🧹 Очищаем кеш и регенерируем...');
    await clearComponentCache(selectedComponentId);
    await clearImageCaches();
    await performRenderRequest(true);
  };

  const handleCancelRequest = () => {
    if (activeRequestController.current) {
      activeRequestController.current.abort();
      activeRequestController.current = null;
    }
  };

  // 🆕 Batch Mode: Генерация всех компонентов группы
  const handleBatchGenerate = async (forceRegenerate = false) => {
    const categoriesToUse =
      selectedBatchCategories.length > 0
        ? selectedBatchCategories
        : categoryFilter !== 'all'
          ? [categoryFilter]
          : [];

    if (categoriesToUse.length === 0) {
      alert('Пожалуйста, выберите хотя бы одну категорию для batch-генерации');
      return;
    }

    const categoryComponents = components.filter(c => categoriesToUse.includes(c.category));
    if (categoryComponents.length === 0) {
      alert('Нет компонентов в выбранной категории');
      return;
    }

    if (forceRegenerate) {
      await clearComponentCache();
      await clearImageCaches();
    }

    setBatchLoading(true);
    batchAbortRef.current = false;
    setBatchProgress({ current: 0, total: categoryComponents.length });
    
    // Инициализируем результаты
    const initialResults: BatchResult[] = categoryComponents.map(c => ({
      componentId: c.id,
      componentName: c.name,
      displayName: c.display_name,
      status: 'pending'
    }));
    setBatchResults(initialResults);

    // Последовательная генерация (можно сделать параллельную с ограничением)
    for (let i = 0; i < categoryComponents.length; i++) {
      if (batchAbortRef.current) {
        break;
      }

      const component = categoryComponents[i];
      
      // Обновляем статус на loading
      setBatchResults(prev => prev.map(r => 
        r.componentId === component.id ? { ...r, status: 'loading' as const } : r
      ));

      try {
        const response = await axios.post('/api/admin/component-tester/render', {
          componentId: component.id,
          theme: selectedTheme,
          contentLanguage: selectedLanguage,
          platform: selectedPlatform,
          geo: selectedGeo,
          forceRegenerate
        });

        const data = response.data;

        if (data.success) {
          setBatchResults(prev => prev.map(r => 
            r.componentId === component.id 
              ? { ...r, status: 'success' as const, generationTime: data.metadata?.generationTime }
              : r
          ));
        } else {
          throw new Error(data.error || 'Ошибка генерации');
        }
      } catch (error: any) {
        setBatchResults(prev => prev.map(r => 
          r.componentId === component.id 
            ? { ...r, status: 'error' as const, error: error.message }
            : r
        ));
      }

      setBatchProgress({ current: i + 1, total: categoryComponents.length });
    }

    setBatchLoading(false);
  };

  const handleBatchCancel = () => {
    batchAbortRef.current = true;
  };

  // Переключение категорий для batch-режима
  const toggleBatchCategory = (category: string) => {
    setSelectedBatchCategories(prev => 
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  // Открыть компонент в новой вкладке (для batch режима)
  const openComponentPreview = async (componentId: number) => {
    // Окно открывается сразу по клику; HTML показывается в sandboxed iframe, не на origin приложения.
    const previewWindow = openSandboxedPreviewWindow('Component Preview');
    if (!previewWindow) {
      alert('Браузер заблокировал новое окно. Разрешите всплывающие окна для этого сайта.');
      return;
    }
    try {
      const params: Record<string, string | number | boolean> = {
        componentId,
        theme: selectedTheme,
        contentLanguage: selectedLanguage,
        platform: selectedPlatform,
        geo: selectedGeo,
        showImageSources: showImageSources
      };
      
      const response = await axios.post('/api/admin/component-tester/preview', params);
      const html = typeof response.data === 'string' ? response.data : '';
      previewWindow.render(html);
    } catch (error) {
      console.error('Ошибка открытия превью:', error);
      previewWindow.close();
      alert('Не удалось открыть превью');
    }
  };

  // Открытие в новом окне
  const handleOpenInNewWindow = async () => {
    if (!selectedComponentId) return;
    
    // Окно открывается сразу по клику; HTML показывается в sandboxed iframe, не на origin приложения.
    const previewWindow = openSandboxedPreviewWindow('Component Preview');
    if (!previewWindow) {
      alert('Браузер заблокировал новое окно. Разрешите всплывающие окна для этого сайта.');
      return;
    }
    try {
      // ✅ ИСПРАВЛЕНИЕ: НЕ передаём forceRegenerate вообще
      // Backend использует дефолт false (кеш), что нам и нужно для Full Preview
      const params: Record<string, string | number | boolean> = {
        componentId: selectedComponentId,
        theme: selectedTheme,
        contentLanguage: selectedLanguage,
        platform: selectedPlatform,
        geo: selectedGeo,
        // 🆕 #F-Background: Параметры глобального фона
        backgroundEnabled: backgroundEnabled,
        backgroundMode: backgroundMode,
        backgroundPerformance: backgroundPerformance,
        ...(backgroundPresetId ? { backgroundPresetId } : {}),
        ...(backgroundCuratedId ? { backgroundCuratedId } : {})
        // forceRegenerate НЕ передаём - backend дефолт false
      };
      const selectedComponent = components.find(c => c.id === selectedComponentId);
      const isGallery = selectedComponent?.category === 'gallery' || selectedComponent?.category === 'portfolio';

      if (isGallery) {
        params.filter = galleryFilter;
        params.page = galleryPage;
        params.limit = galleryLimit;
      }
      
      const response = await axios.post('/api/admin/component-tester/preview', params);
      const html = typeof response.data === 'string' ? response.data : '';
      previewWindow.render(html);
    } catch (error) {
      console.error('Ошибка открытия превью:', error);
      previewWindow.close();
      alert('Не удалось открыть превью');
    }
  };

  // Навигация
  const handlePrevious = () => {
    const currentIndex = filteredComponents.findIndex(c => c.id === selectedComponentId);
    if (currentIndex > 0) {
      setSelectedComponentId(filteredComponents[currentIndex - 1].id);
      setRenderedData(null); // Очищаем предыдущий рендер
    }
  };

  const handleNext = () => {
    const currentIndex = filteredComponents.findIndex(c => c.id === selectedComponentId);
    if (currentIndex < filteredComponents.length - 1) {
      setSelectedComponentId(filteredComponents[currentIndex + 1].id);
      setRenderedData(null); // Очищаем предыдущий рендер
    }
  };

  const canGoPrevious = () => {
    const currentIndex = filteredComponents.findIndex(c => c.id === selectedComponentId);
    return currentIndex > 0;
  };

  const canGoNext = () => {
    const currentIndex = filteredComponents.findIndex(c => c.id === selectedComponentId);
    return currentIndex >= 0 && currentIndex < filteredComponents.length - 1;
  };

  // Фильтрация компонентов
  const filteredComponents = useMemo(() => components.filter(component => {
    const matchesSearch = component.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         component.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || component.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  }), [components, searchQuery, categoryFilter]);

  // Прогресс (для простоты считаем что протестированы все просмотренные)
  const currentIndex = filteredComponents.findIndex(c => c.id === selectedComponentId);
  const progress = selectedComponentId ? ((currentIndex + 1) / filteredComponents.length * 100) : 0;

  const selectedCategoriesForBatch = selectedBatchCategories.length > 0
    ? selectedBatchCategories
    : (categoryFilter !== 'all' ? [categoryFilter] : []);
  const batchComponentsCount = selectedCategoriesForBatch.length > 0
    ? components.filter(c => selectedCategoriesForBatch.includes(c.category)).length
    : 0;

  return (
    <div className="component-tester">
      <header className="component-tester__header">
        <h1>Тестер компонентов</h1>
        <p>Индивидуальное тестирование компонентов с реальной генерацией контента</p>
      </header>

      {/* MODE SELECTOR */}
      <section className="component-tester__section" style={{ padding: '15px 20px' }}>
        <div className="u2-row" style={{ justifyContent: 'space-between' }}>
          <span className="u2-muted" style={{ fontSize: 12 }}>Режим</span>
          <div className="u2-toolbarGroup">
            <button
              type="button"
              className={`u2-btn ${testMode === 'single' ? 'is-primary' : 'is-secondary'}`}
              onClick={() => setTestMode('single')}
            >
              Одиночный
            </button>
            <button
              type="button"
              className={`u2-btn ${testMode === 'batch' ? 'is-primary' : 'is-secondary'}`}
              onClick={() => setTestMode('batch')}
            >
              Пакетный
            </button>
          </div>
        </div>
      </section>

      {/* SELECT COMPONENT */}
      <section className="component-tester__section">
        <h3>Выбор компонента</h3>
        
        <div className="component-select">
          <select
            value={selectedComponentId || ''}
            onChange={(e) => {
              setSelectedComponentId(e.target.value ? parseInt(e.target.value) : null);
              setRenderedData(null);
            }}
            className="u2-select"
          >
            <option value="">-- Выберите компонент ({filteredComponents.length}) --</option>
            {filteredComponents.map(component => (
              <option key={component.id} value={component.id}>
                {component.display_name}
              </option>
            ))}
          </select>
        </div>

        <div className="component-filters">
          <div className="quick-filters">
            <button
              type="button"
              className={`u2-btn ${categoryFilter === 'all' ? 'is-primary' : 'is-ghost'}`}
              onClick={() => setCategoryFilter('all')}
            >
              Все
            </button>
            {categories.map(category => (
              <button
                key={category}
                type="button"
                className={`u2-btn ${categoryFilter === category ? 'is-primary' : 'is-ghost'}`}
                onClick={() => setCategoryFilter(category)}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>

          <div className="search-box">
            <input
              type="text"
              placeholder="Поиск компонентов…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="u2-input"
            />
            <span className="search-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16z" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
            </span>
          </div>

          {testMode === 'batch' && (
            <div className="batch-category-selector" style={{ marginTop: '10px' }}>
              <p style={{ margin: '0 0 6px 0', fontWeight: 600 }}>Категории для batch:</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {categories.map(category => (
                  <label key={category} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px' }}>
                    <input
                      type="checkbox"
                      checked={selectedBatchCategories.includes(category)}
                      onChange={() => toggleBatchCategory(category)}
                    />
                    <span>{category.charAt(0).toUpperCase() + category.slice(1)}</span>
                  </label>
                ))}
              </div>
              <small className="u2-muted" style={{ fontSize: 12 }}>
                Если ничего не выбрано — будет использован текущий фильтр категории.
              </small>
            </div>
          )}
        </div>
      </section>

      {/* SETTINGS */}
      <section className="component-tester__section">
        <h3>Настройки</h3>
        
        <div className="settings-grid">
          <div className="setting-item">
            <label>Тема:</label>
            <select
              value={selectedTheme}
              onChange={(e) => setSelectedTheme(e.target.value)}
              className="u2-select"
            >
              {themes.map(theme => (
                <option key={theme.value} value={theme.value}>
                  {theme.label}
                </option>
              ))}
            </select>
          </div>

          <div className="setting-item">
            <label>Язык:</label>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="u2-select"
            >
              <option value="ru">Русский</option>
              <option value="en">Английский</option>
              <option value="de">Немецкий</option>
              <option value="fr">Французский</option>
              <option value="it">Итальянский</option>
            </select>
          </div>

          <div className="setting-item">
            <label>Платформа:</label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="u2-select"
            >
              <option value="facebook">Facebook</option>
              <option value="google">Google</option>
              <option value="tiktok">TikTok</option>
              <option value="linkedin">LinkedIn</option>
            </select>
          </div>

          <div className="setting-item">
            <label>Geo:</label>
            <select
              value={selectedGeo}
              onChange={(e) => setSelectedGeo(e.target.value)}
              className="u2-select"
            >
              <option value="eu">EU</option>
              <option value="us">US</option>
              <option value="ru">RU</option>
              <option value="cn">CN</option>
            </select>
          </div>
          
          {/* 🎯 #F-084 MVP: Включение метрик производительности */}
          <div className="setting-item">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={includeMetrics}
                onChange={(e) => setIncludeMetrics(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span title="Собирает детальную информацию о времени генерации изображений, текстов и рендеринга">
                Метрики производительности
              </span>
            </label>
          </div>
          
        </div>
        
        {/* 🆕 #F-Background: СЕКЦИЯ ГЛОБАЛЬНОГО ФОНА */}
        <div className="background-section" style={{ 
          marginTop: '20px', 
          padding: '15px 20px', 
          background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)', 
          borderRadius: '10px', 
          border: '2px solid #667eea40'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h4 style={{ margin: 0, color: '#667eea', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Глобальный фон
            </h4>
            <a 
              href="/admin/background-tester" 
              target="_blank"
              style={{ 
                fontSize: '12px', 
                color: '#667eea', 
                textDecoration: 'none',
                padding: '4px 10px',
                background: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '4px',
                border: '1px solid rgba(102, 126, 234, 0.3)'
              }}
            >
              🔧 Open Background Tester →
            </a>
          </div>
          
          <div className="settings-grid">
            <div className="setting-item">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={backgroundEnabled}
                  onChange={(e) => setBackgroundEnabled(e.target.checked)}
                  style={{ 
                    cursor: 'pointer', 
                    width: '18px', 
                    height: '18px',
                    accentColor: '#667eea'
                  }}
                />
                <span style={{ fontWeight: 500 }}>Enable Background in Preview</span>
              </label>
            </div>
            
            {backgroundEnabled && (
              <>
                <div className="setting-item">
                  <label>Mode:</label>
                  <select
                    value={backgroundMode}
                    onChange={(e) => setBackgroundMode(e.target.value as 'static' | 'dynamic')}
                    style={{ borderColor: '#667eea40' }}
                  >
                    <option value="static">🖼️ Static (Pattern/Texture)</option>
                    <option value="dynamic">✨ Dynamic (Animated)</option>
                  </select>
                </div>
                
                {backgroundMode === 'dynamic' && (
                  <div className="setting-item">
                    <label>Performance:</label>
                    <select
                      value={backgroundPerformance}
                      onChange={(e) => setBackgroundPerformance(e.target.value)}
                      className="u2-select"
                    >
                      <option value="lightweight">Лёгкий</option>
                      <option value="balanced">Сбалансированный</option>
                      <option value="visual_first">Лучшее качество</option>
                    </select>
                  </div>
                )}
                
                {backgroundMode === 'dynamic' && (
                  <div className="setting-item">
                    <label>Dynamic preset:</label>
                    <select
                      value={backgroundPresetId || ''}
                      onChange={(e) => setBackgroundPresetId(e.target.value ? Number(e.target.value) : null)}
                      className="u2-select"
                    >
                      <option value="">Авто (по правилам)</option>
                      {bgPresets.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.subtype}) • вес: {p.performance_weight}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {backgroundMode === 'static' && (
                  <div className="setting-item">
                    <label>Static image:</label>
                    <select
                      value={backgroundCuratedId || ''}
                      onChange={(e) => setBackgroundCuratedId(e.target.value ? Number(e.target.value) : null)}
                      className="u2-select"
                    >
                      <option value="">Авто (по правилам)</option>
                      {bgCurated.map(bg => (
                        <option key={bg.id} value={bg.id}>
                          {bg.name} ({bg.type}) {bg.color_scheme}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}
          </div>
          
          {backgroundEnabled && (
            <div style={{ 
              marginTop: '12px', 
              padding: '10px', 
              background: 'rgba(102, 126, 234, 0.1)', 
              borderRadius: '6px',
              fontSize: '12px',
              color: '#667eea'
            }}>
              💡 <strong>Tip:</strong> Use "Open Full Preview" button to see the background effect. 
              Background is visible on components with transparent/semi-transparent backgrounds.
            </div>
          )}
        </div>
        
        {/* Параметры фильтрации для Gallery компонентов */}
        {selectedComponentId && components.find(c => c.id === selectedComponentId)?.category === 'gallery' && (
          <div
            className="gallery-filter-section"
            style={{
              marginTop: '20px',
              padding: '15px',
              background: 'var(--u2-surface-3)',
              borderRadius: '12px',
              border: '1px solid var(--u2-border)',
            }}
          >
            <h4 className="u2-strong" style={{ margin: '0 0 15px 0' }}>
              Gallery Filters (Test API #F-073)
            </h4>
            <div className="settings-grid">
              <div className="setting-item">
                <label>Filter by Category:</label>
                <select
                  value={galleryFilter}
                  onChange={(e) => setGalleryFilter(e.target.value)}
                  className="u2-select"
                >
                  <option value="all">All Categories</option>
                  {renderedData?.generatedContent?.filters?.items?.map((filter: any) => (
                    <option key={filter.slug} value={filter.slug}>
                      {filter.label}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="setting-item">
                <label>Page:</label>
                <input
                  type="number"
                  min="1"
                  value={galleryPage}
                  onChange={(e) => setGalleryPage(parseInt(e.target.value) || 1)}
                  className="u2-input"
                  style={{ width: '80px' }}
                />
              </div>
              
              <div className="setting-item">
                <label>Items per page:</label>
                <select
                  value={galleryLimit}
                  onChange={(e) => setGalleryLimit(parseInt(e.target.value))}
                  className="u2-select"
                >
                  <option value="3">3</option>
                  <option value="6">6</option>
                  <option value="9">9</option>
                  <option value="12">12</option>
                  <option value="15">15</option>
                </select>
              </div>
            </div>
            
            {renderedData?.pagination && (
              <div
                style={{
                  marginTop: '10px',
                  padding: '10px',
                  background: 'var(--u2-surface-1)',
                  border: '1px solid var(--u2-border)',
                  borderRadius: '12px',
                  fontSize: '13px'
                }}
              >
                <strong>Pagination Info:</strong> Page {renderedData.pagination.currentPage} of {renderedData.pagination.totalPages} | 
                Total: {renderedData.pagination.total} | 
                Showing: {renderedData.generatedContent.items?.length || 0} items
              </div>
            )}
          </div>
        )}

        <div className="settings-actions">
          {testMode === 'single' ? (
            <>
              <button
                type="button"
                className="u2-btn is-primary"
                onClick={handleGenerate}
                disabled={!selectedComponentId || loading}
              >
                {loading ? 'Генерация…' : 'Сгенерировать компонент'}
              </button>
              
              <button
                type="button"
                className="u2-btn is-secondary"
                onClick={handleRegenerate}
                disabled={!selectedComponentId || loading}
                title="Полная регенерация без кеша (очищает кеш перед запросом)"
              >
                Пересобрать (без кеша)
              </button>
              
              {renderedData && (
                <button
                  type="button"
                  className="u2-btn is-ghost"
                  onClick={handleOpenInNewWindow}
                  disabled={loading}
                  title="Открыть превью в новом окне"
                >
                  Открыть full preview
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className="u2-btn is-primary"
                onClick={() => handleBatchGenerate(false)}
                disabled={batchComponentsCount === 0 || batchLoading}
              >
                {batchLoading
                  ? `Генерация ${batchProgress.current}/${batchProgress.total}…`
                  : `Сгенерировать категории (${batchComponentsCount})`}
              </button>
              
              <button
                type="button"
                className="u2-btn is-secondary"
                onClick={() => handleBatchGenerate(true)}
                disabled={batchComponentsCount === 0 || batchLoading}
                title="Полная регенерация выбранных категорий без кеша"
              >
                Пересобрать выбранные (без кеша)
              </button>

              {batchLoading && (
                <button
                  type="button"
                  className="u2-btn is-ghost"
                  onClick={handleBatchCancel}
                >
                  Остановить
                </button>
              )}
            </>
          )}
        </div>
      </section>

      {/* BATCH RESULTS */}
      {testMode === 'batch' && batchResults.length > 0 && (
        <section className="component-tester__section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0 }}>Результаты пакетной генерации ({batchResults.filter(r => r.status === 'success').length}/{batchResults.length})</h3>
            <label className="checkbox-label source-toggle" title="Показать источник каждого изображения в превью">
              <input 
                type="checkbox" 
                checked={showImageSources} 
                onChange={(e) => setShowImageSources(e.target.checked)}
              />
              <span>📷 Показать источники</span>
            </label>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gap: '10px',
            maxHeight: '500px',
            overflowY: 'auto',
            padding: '10px'
          }}>
            {batchResults.map(result => (
              <div 
                key={result.componentId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 15px',
                  background:
                    result.status === 'success'
                      ? 'hsl(145 72% 45% / 0.14)'
                      : result.status === 'error'
                        ? 'hsl(355 85% 58% / 0.14)'
                        : result.status === 'loading'
                          ? 'hsl(226 92% 62% / 0.14)'
                          : 'var(--u2-surface-3)',
                  borderRadius: '8px',
                  border: `1px solid ${
                    result.status === 'success'
                      ? 'hsl(145 72% 45% / 0.38)'
                      : result.status === 'error'
                        ? 'hsl(355 85% 58% / 0.38)'
                        : result.status === 'loading'
                          ? 'hsl(226 92% 62% / 0.38)'
                          : 'var(--u2-border)'
                  }`
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '18px' }}>
                    {result.status === 'pending' && '·'}
                    {result.status === 'loading' && '…'}
                    {result.status === 'success' && 'OK'}
                    {result.status === 'error' && '!'}
                  </span>
                  <div>
                    <div style={{ fontWeight: '500' }}>{result.displayName}</div>
                    {result.generationTime && (
                      <div style={{ fontSize: '12px', color: 'var(--u2-muted)' }}>
                        ⏱️ {result.generationTime}ms
                      </div>
                    )}
                    {result.error && (
                      <div style={{ fontSize: '12px', color: 'var(--u2-danger)' }}>
                        {result.error}
                      </div>
                    )}
                  </div>
                </div>
                
                {result.status === 'success' && (
                  <button
                    type="button"
                    onClick={() => openComponentPreview(result.componentId)}
                    className="u2-btn is-secondary"
                  >
                    Открыть превью
                  </button>
                )}
              </div>
            ))}
          </div>
          
          {/* Progress bar */}
          {batchLoading && (
            <div style={{ marginTop: '15px' }}>
              <div style={{ 
                height: '8px', 
                background: 'var(--u2-border)', 
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${(batchProgress.current / batchProgress.total) * 100}%`,
                  background: 'var(--u2-grad-accent)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{ textAlign: 'center', marginTop: '5px', fontSize: '13px', color: 'var(--u2-muted)' }}>
                {batchProgress.current} / {batchProgress.total}
              </div>
            </div>
          )}
        </section>
      )}

      {/* PREVIEW (только в single режиме) */}
      {testMode === 'single' && loading ? (
        <section className="component-tester__section preview-section loading">
          <div className="loader">
            <div className="spinner"></div>
            <p>{loadingMessage}</p>
            <small>Прошло {loadingElapsed} cек.</small>
            <button
              className="u2-btn is-ghost"
              onClick={handleCancelRequest}
              type="button"
            >
              Остановить
            </button>
          </div>
        </section>
      ) : testMode === 'single' && renderedData ? (
        <>
          <section className="component-tester__section preview-section">
            <div className="preview-header">
              <h3>PREVIEW</h3>
              <div className="preview-info">
                <span className="info-badge">⏱️ {renderedData.metadata.generationTime}ms</span>
                <span className={`info-badge ${renderedData.metadata.cached ? 'cached' : 'fresh'}`}>
                  {renderedData.metadata.cached ? '💾 Cached' : '✨ Fresh'}
                </span>
                <button 
                  className="btn-icon"
                  onClick={handleOpenInNewWindow}
                  title="Open in New Window"
                >
                  🪟 Open Full Preview
                </button>
                
                <label className="checkbox-label source-toggle" title="Показать источник каждого изображения">
                  <input 
                    type="checkbox" 
                    checked={showImageSources} 
                    onChange={(e) => setShowImageSources(e.target.checked)}
                  />
                  <span>📷 Показать источники</span>
                </label>
              </div>
            </div>
            
            <div className={`preview-container ${showImageSources ? 'show-image-sources' : ''}`}>
              <iframe
                ref={previewFrameRef}
                className="component-render component-render--frame"
                title={`Превью компонента ${renderedData.component?.name || ''}`.trim()}
                sandbox={PREVIEW_SANDBOX}
                srcDoc={previewSrcDoc}
                style={{ height: previewFrameHeight }}
              />
            </div>
          </section>

          {/* DETAILS */}
          <section className="component-tester__section details-section">
            <div 
              className="details-header"
              onClick={() => setShowDetails(!showDetails)}
            >
              <h3>Детали</h3>
              <span className="toggle-icon">{showDetails ? '▼' : '▶'} {showDetails ? 'Hide' : 'Show'}</span>
            </div>

            {showDetails && (
              <div className="details-content">
                <div className="detail-block">
                  <h4>Сгенерированный контент:</h4>
                  <pre>{JSON.stringify(renderedData.generatedContent, null, 2)}</pre>
                </div>

                <div className="detail-block">
                  <h4>Images ({Object.keys(renderedData.images).length}):</h4>
                  <div className="images-list">
                    {Object.entries(renderedData.images).map(([key, imageData]: [string, any]) => {
                      // Обрабатываем как массив (per_item) или одиночное изображение
                      const images = Array.isArray(imageData) ? imageData : [imageData];
                      
                      return images.map((image: any, index: number) => (
                        <div key={`${key}-${index}`} className="image-item">
                          <span className="image-key">
                            {key}{images.length > 1 ? `[${index}]` : ''}:
                          </span>
                          
                          <a 
                            href={image.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="image-link"
                            title="Click to open image"
                          >
                            {image.processed ? (
                              <span className="status success">
                                Обработано ({image.width}x{image.height})
                              </span>
                            ) : image.source === 'picsum_placeholder' ? (
                              <span className="status warning">
                                ⚠️ Placeholder ({image.width}x{image.height})
                              </span>
                            ) : image.source === 'placeholder' ? (
                              <span className="status warning">
                                ⚠️ Not found (placeholder)
                              </span>
                            ) : (
                              <span className="status info">
                                📷 Original ({image.width}x{image.height})
                              </span>
                            )}
                            <span className="view-hint">Открыть</span>
                          </a>
                        </div>
                      ));
                    })}
                  </div>
                </div>

                <div className="detail-block">
                  <h4>Performance:</h4>
                  <ul className="performance-list">
                    <li>AI Generation: <strong>{renderedData.metadata.aiGenerationTime}ms</strong></li>
                    <li>Image Search: <strong>{renderedData.metadata.imageSearchTime}ms</strong></li>
                    <li>Image Processing: <strong>{renderedData.metadata.imageProcessingTime}ms</strong></li>
                    <li className="total">Total: <strong>{renderedData.metadata.generationTime}ms</strong></li>
                  </ul>
                </div>

                {/* 🎯 #F-084 MVP: Метрики производительности */}
                {renderedData.metrics && (
                  <div className="detail-block" style={{ background: '#f0f8ff', border: '2px solid #0066cc' }}>
                    <h4 style={{ color: '#0066cc' }}>Метрики производительности (детально)</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#e0f0ff' }}>
                          <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #b0d4f1' }}>Stage</th>
                          <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #b0d4f1' }}>Duration</th>
                          <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #b0d4f1' }}>% Total</th>
                          <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #b0d4f1' }}>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #b0d4f1' }}>Изображения</td>
                          <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #b0d4f1', fontWeight: 'bold' }}>
                            {renderedData.metrics.stages.images.duration}ms
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #b0d4f1' }}>
                            {renderedData.metrics.stages.images.percentage}%
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #b0d4f1', fontSize: '12px' }}>
                            {renderedData.metrics.stages.images.attempts} attempts, 
                            {renderedData.metrics.stages.images.totalRequested} requested, 
                            {renderedData.metrics.stages.images.totalReceived} received, 
                            {renderedData.metrics.stages.images.totalDuplicates} duplicates
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #b0d4f1' }}>📝 Texts</td>
                          <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #b0d4f1', fontWeight: 'bold' }}>
                            {renderedData.metrics.stages.texts.duration}ms
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #b0d4f1' }}>
                            {renderedData.metrics.stages.texts.percentage}%
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #b0d4f1' }}>AI content generation</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', border: '1px solid #b0d4f1' }}>Рендеринг</td>
                          <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #b0d4f1', fontWeight: 'bold' }}>
                            {renderedData.metrics.stages.rendering.duration}ms
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #b0d4f1' }}>
                            {renderedData.metrics.stages.rendering.percentage}%
                          </td>
                          <td style={{ padding: '8px', border: '1px solid #b0d4f1' }}>HTML/CSS generation</td>
                        </tr>
                        <tr style={{ background: '#e0f0ff', fontWeight: 'bold' }}>
                          <td style={{ padding: '8px', border: '1px solid #b0d4f1' }}>TOTAL</td>
                          <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #b0d4f1' }}>
                            {renderedData.metrics.totalDuration}ms
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #b0d4f1' }}>100%</td>
                          <td style={{ padding: '8px', border: '1px solid #b0d4f1', fontSize: '11px' }}>
                            {new Date(renderedData.metrics.startTime).toLocaleTimeString()} → {new Date(renderedData.metrics.endTime).toLocaleTimeString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* NAVIGATION */}
          <section className="component-tester__section navigation-section">
            <h3>NAVIGATION</h3>
            
            <div className="navigation-buttons">
              <button
                onClick={handlePrevious}
                disabled={!canGoPrevious()}
                className="btn-nav"
              >
                ← Previous
              </button>
              
              <span className="current-position">
                {currentIndex + 1} / {filteredComponents.length}
              </span>
              
              <button
                onClick={handleNext}
                disabled={!canGoNext()}
                className="btn-nav"
              >
                Next →
              </button>
            </div>

            <div className="progress-bar-container">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="progress-text">{Math.round(progress)}%</span>
            </div>
          </section>
        </>
      ) : testMode === 'single' ? (
        <section className="component-tester__section preview-section empty">
          <div className="empty-state">
            <p className="empty-icon">👆</p>
            <p className="empty-text">Выберите компонент и нажмите «Сгенерировать компонент», чтобы начать тестирование</p>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default ComponentTester;

